import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Extracts bucket and path from a Supabase storage URL.
 */
function parseStorageUrl(url: string): { bucket: string; path: string } | null {
    try {
        const match = url.match(/\/storage\/v1\/object\/(?:public|authenticated)\/([^/]+)\/(.+)/);
        if (match) {
            return { bucket: match[1], path: decodeURIComponent(match[2]) };
        }
    } catch {
        // ignore parse errors
    }
    return null;
}

const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();
const EXPIRY_SECONDS = 3600;
const BUFFER_SECONDS = 300;

export interface SignedUrlState {
    /** The URL to load: signed, or the input itself when it needs no signing. Undefined while signing or after a failure. */
    url: string | undefined;
    /** Signing failed, so `url` will not arrive without a retry. */
    failed: boolean;
    /** Forget the cached signature and sign again (also the cure for one that expired on an open page). */
    retry: () => void;
}

function cachedUrl(storageUrl: string | null | undefined): string | undefined {
    if (!storageUrl) return undefined;
    if (!storageUrl.includes('/storage/v1/object/')) return storageUrl;
    const cached = signedUrlCache.get(storageUrl);
    if (cached && Date.now() < cached.expiresAt) return cached.url;
    return undefined;
}

/**
 * React hook that converts a Supabase storage URL into a signed URL, and says when signing failed,
 * so a caller can tell a slow photo from a missing one and offer a retry.
 */
export function useSignedUrlState(storageUrl: string | null | undefined): SignedUrlState {
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<{ url: string | undefined; failed: boolean }>(() => ({
        url: cachedUrl(storageUrl),
        failed: false,
    }));

    useEffect(() => {
        // Keeps the current state object when nothing changed, so a cached photo renders once.
        const settle = (url: string | undefined, failed: boolean) =>
            setState(s => (s.url === url && s.failed === failed ? s : { url, failed }));

        if (!storageUrl) { settle(undefined, false); return; }
        if (!storageUrl.includes('/storage/v1/object/')) { settle(storageUrl, false); return; }

        const cached = signedUrlCache.get(storageUrl);
        if (cached && Date.now() < cached.expiresAt) { settle(cached.url, false); return; }

        const parsed = parseStorageUrl(storageUrl);
        if (!parsed) { settle(storageUrl, false); return; }

        let cancelled = false;
        settle(undefined, false);
        supabase.storage
            .from(parsed.bucket)
            .createSignedUrl(parsed.path, EXPIRY_SECONDS)
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error || !data?.signedUrl) { settle(undefined, true); return; }
                signedUrlCache.set(storageUrl, {
                    url: data.signedUrl,
                    expiresAt: Date.now() + (EXPIRY_SECONDS - BUFFER_SECONDS) * 1000,
                });
                settle(data.signedUrl, false);
            }, () => {
                if (!cancelled) settle(undefined, true);
            });

        return () => { cancelled = true; };
    }, [storageUrl, attempt]);

    const retry = useCallback(() => {
        if (storageUrl) signedUrlCache.delete(storageUrl);
        setAttempt(n => n + 1);
    }, [storageUrl]);

    return { url: state.url, failed: state.failed, retry };
}

/**
 * React hook that converts a Supabase storage URL into a signed URL.
 */
export function useSignedUrl(storageUrl: string | null | undefined): string | undefined {
    return useSignedUrlState(storageUrl).url;
}
